/**
 * features/admin/AnnouncementsManager.tsx
 * ------------------------------------------------------------------
 * Admin screen to create, edit, and delete announcements.
 * Announcements display on the dashboard for targeted audiences.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';
// Batch B: undefined-safe write + save modal
import { addDoc, updateDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Pin,
  Clock,
  AlertCircle,
  CheckCircle2,
  X,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { stripUndefined } from '@/lib/sanitize';
import type {
  Announcement,
  AnnouncementTarget,
  AnnouncementPriority,
} from '@/types';

const TARGET_LABELS: Record<AnnouncementTarget, string> = {
  all: 'Everyone',
  staff: 'All Staff',
  parents: 'Parents Only',
  admin: 'Administrators',
  teachers: 'All Teachers',
  classTeachers: 'Class Teachers',
  hod: 'Heads of Department',
};

const PRIORITY_STYLES: Record<AnnouncementPriority, string> = {
  normal: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
  important: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
  urgent: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300',
};

interface FormState {
  title: string;
  body: string;
  target: AnnouncementTarget;
  priority: AnnouncementPriority;
  expiresAt: string;
  pinned: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  body: '',
  target: 'all',
  priority: 'normal',
  expiresAt: '',
  pinned: false,
};

export default function AnnouncementsManager() {
  const { userData } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, COLLECTIONS.announcements), orderBy('createdAt', 'desc')),
      );
      setAnnouncements(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Announcement));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAnnouncements(); }, [loadAnnouncements]);

  function openNew() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(a: Announcement) {
    setEditingId(a.id);
    setForm({
      title: a.title,
      body: a.body,
      target: a.target,
      priority: a.priority,
      expiresAt: a.expiresAt ?? '',
      pinned: a.pinned ?? false,
    });
    setShowForm(true);
  }

  async function save() {
    if (!form.title.trim()) {
      setMsg({ ok: false, text: 'Title is required.' });
      return;
    }
    if (!form.body.trim()) {
      setMsg({ ok: false, text: 'Body is required.' });
      return;
    }
    setSaving(true);
    setSaveModal({
      show: true,
      status: 'saving',
      message: editingId ? 'Updating announcement…' : 'Creating announcement…',
    });
    try {
      const now = new Date().toISOString();
      const payload = stripUndefined({
        title: form.title.trim(),
        body: form.body.trim(),
        target: form.target,
        priority: form.priority,
        expiresAt: form.expiresAt || undefined,
        pinned: form.pinned,
        updatedAt: now,
      });

      if (editingId) {
        await updateDoc(doc(db, COLLECTIONS.announcements, editingId), payload);
        setSaveModal({ show: true, status: 'success', message: 'Announcement updated.' });
      } else {
        await addDoc(collection(db, COLLECTIONS.announcements), {
          ...payload,
          dismissedBy: [],
          createdBy: userData?.uid ?? '',
          createdByName: userData?.displayName ?? '',
          createdAt: now,
        });
        setSaveModal({ show: true, status: 'success', message: 'Announcement created.' });
      }
      setShowForm(false);
      setEditingId(null);
      await loadAnnouncements();
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.announcements, id));
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
      setConfirmDelete(null);
      setMsg({ ok: true, text: 'Announcement deleted.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  }

  async function togglePin(a: Announcement) {
    await updateDoc(doc(db, COLLECTIONS.announcements, a.id), { pinned: !a.pinned });
    await loadAnnouncements();
  }

  const isExpired = (a: Announcement) => a.expiresAt && new Date(a.expiresAt) < new Date();
  const activeCount = announcements.filter((a) => !isExpired(a)).length;

  if (loading) return <Spinner label="Loading announcements…" />;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Megaphone}
        title="Announcements"
        subtitle={`${activeCount} active · Manage school-wide and targeted notices`}
      />

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {msg.text}
          </div>
        </SoftCard>
      )}

      {/* Actions */}
      <div className="flex justify-end">
        <AccentButton icon={Plus} onClick={openNew}>New Announcement</AccentButton>
      </div>

      {/* Form */}
      {showForm && (
        <SoftCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </h3>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Title</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Announcement title"
                className={glassFieldClass('')}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Body</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Announcement content…"
                rows={4}
                className={glassFieldClass('')}
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Audience</label>
                <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value as AnnouncementTarget })} className={glassFieldClass('')}>
                  {Object.entries(TARGET_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as AnnouncementPriority })} className={glassFieldClass('')}>
                  <option value="normal">Normal</option>
                  <option value="important">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Expires</label>
                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                  className={glassFieldClass('')}
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.pinned}
                    onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  Pin to top
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <AccentButton variant="soft" onClick={() => setShowForm(false)}>Cancel</AccentButton>
              <AccentButton onClick={save} disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update' : 'Publish'}
              </AccentButton>
            </div>
          </div>
        </SoftCard>
      )}

      {/* List */}
      {announcements.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-gray-500 text-center py-6">No announcements yet. Create one to get started.</p>
        </SoftCard>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => {
            const expired = isExpired(a);
            return (
              <SoftCard key={a.id} pad="p-4">
                <div className={`flex items-start gap-3 ${expired ? 'opacity-50' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {a.pinned && <Pin size={13} className="text-amber-500" />}
                      <h4 className="font-semibold text-sm text-gray-900 dark:text-white">{a.title}</h4>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${PRIORITY_STYLES[a.priority]}`}>
                        {a.priority}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {TARGET_LABELS[a.target]}
                      </span>
                      {expired && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Expired</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap line-clamp-3">{a.body}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-2">
                      <span>By {a.createdByName}</span>
                      <span>{new Date(a.createdAt).toLocaleDateString()}</span>
                      {a.expiresAt && <span className="flex items-center gap-1"><Clock size={11} /> Expires {a.expiresAt}</span>}
                      {a.dismissedBy && a.dismissedBy.length > 0 && (
                        <span>{a.dismissedBy.length} dismissed</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => togglePin(a)} className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${a.pinned ? 'text-amber-500' : 'text-gray-400'}`} title={a.pinned ? 'Unpin' : 'Pin'}>
                      <Pin size={14} />
                    </button>
                    <button onClick={() => openEdit(a)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit">
                      <Pencil size={14} />
                    </button>
                    {confirmDelete === a.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(a.id)} className="px-2 py-1 text-xs bg-red-500 text-white rounded-lg">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 text-xs text-gray-500">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDelete(a.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-400" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </SoftCard>
            );
          })}
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
